import React, {useState} from 'react';
import {Grid, IconButton, Typography, Box} from '@mui/material';
import {Delete, Edit, Visibility, ErrorOutline} from '@mui/icons-material';

import {ReportPackFormFragment} from '../../schema-types/ReportPackFormFragment';
import {FormTypes} from '../../schema-types/globalTypes';
import Theme, {themeColors} from '../../theme/Theme';

import {
  FormStatus,
  getReportPackDisabled,
  ReportPackContactProps,
} from './ReportPackHelpers';
import {ScrollableModal} from '../../components/Common/ScrollableModal';

export const ReportPackExistingFormsList: React.FC<
  ReportPackContactProps & {
    onEdit: (data: ReportPackFormFragment) => void;
    onDelete: (data: ReportPackFormFragment) => void;
    disabled?: boolean;
  }
> = ({contact, report, onDelete, onEdit, disabled}) => {
  const forms = report.forms
    .filter((form) => form.contactCuroId === contact.curoId || !form.contactCuroId)
    .sort((a, b) => {
      if ((a?.formTypeLabel ?? '') < (b?.formTypeLabel ?? '')) {
        return -1;
      }
      if ((a?.formTypeLabel ?? '') > (b?.formTypeLabel ?? '')) {
        return 1;
      }
      return 0;
    });
  const [selectedMissingFieldsModal, setSelectedMissingFieldsModal] = useState<
    (string | null)[]
  >([]);

  if (!forms.length) {
    return (
      <Box
        bgcolor={themeColors.greyLight}
        display="flex"
        alignContent="center"
        justifyContent="center"
        p={3}
        pt={2}
        borderRadius={2}
      >
        <Typography>No forms for {contact.name}</Typography>
      </Box>
    );
  }
  return (
    <>
      <Grid gap={2} container direction="column">
        {forms.map((form) => {
          const hasFormDetails =
            form.formType !== FormTypes.MULTREES_DDM &&
            form.formType !== FormTypes.BANK_DETAIL_COLLECTION;

          return (
            <Grid item xs={12} key={form.id}>
              <Box
                bgcolor={Theme.palette.background.default}
                borderRadius={2}
                p={2}
                gap={2}
                display="flex"
              >
                <Box pl={2} flex={1} display="flex" alignItems="center" gap={3}>
                  <Typography>
                    <b>{form.formTypeLabel}</b> v.{form.version}
                  </Typography>
                  {form.statusAndMissing && (
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <FormStatus formStatus={form.statusAndMissing.status} />
                    </Box>
                  )}
                </Box>
                <Box flex={0} display="flex">
                  {getReportPackDisabled(report) ? (
                    hasFormDetails && (
                      <IconButton title="Edit" onClick={() => onEdit(form)}>
                        <Visibility />
                      </IconButton>
                    )
                  ) : (
                    <>
                      {form.statusAndMissing?.missingFields?.length ? (
                        <IconButton
                          onClick={() =>
                            setSelectedMissingFieldsModal(
                              form.statusAndMissing?.missingFields ?? []
                            )
                          }
                        >
                          <ErrorOutline color="error" />
                        </IconButton>
                      ) : null}
                      {hasFormDetails && (
                        <IconButton title="Edit" onClick={() => onEdit(form)}>
                          <Edit />
                        </IconButton>
                      )}
                      <IconButton
                        disabled={disabled}
                        onClick={() => (disabled ? {} : onDelete(form))}
                        data-testid="delete-form"
                      >
                        <Delete />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
      <ScrollableModal
        autoHeight
        open={selectedMissingFieldsModal.length > 0}
        onClose={() => setSelectedMissingFieldsModal([])}
      >
        <>
          <Typography variant="h5">Missing or Invalid Field(s):</Typography>
          <ul>
            {selectedMissingFieldsModal.map((f) => {
              return <li key={f}>{f?.replaceAll('_', ' ')}</li>;
            })}
          </ul>
        </>
      </ScrollableModal>
    </>
  );
};
